const ownHandles = ['eisdieler', 'bowler-berlin', 'directors-cut', 'laconch'];
const categoryOrder = ['alle-bekleidung', 'alle-schuhe', 'alle-accessoires', 'bundles', 'top-brands', 'gadgets', 'nachhaltigkeit'];

export function safeUrl(value) {
  const url = String(value || '').trim();
  if (!url || /[\u0000-\u001f\u007f]/.test(url)) return '';
  try {
    const parsed = new URL(url, 'https://eisdieler.org');
    return ['https:', 'http:', 'mailto:', 'tel:'].includes(parsed.protocol) ? url : '';
  } catch { return ''; }
}

export function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));
}

function handle(link) {
  try { return new URL(link.url, 'https://eisdieler.org').pathname.split('/').filter(Boolean).pop() || ''; }
  catch { return ''; }
}

function normalize(value) {
  return String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[’‘´]/g, "'").toLowerCase().trim();
}

function mergeLinks(input) {
  const result = [];
  for (const item of Array.isArray(input) ? input : []) {
    const title = String(item.title || '').trim();
    const children = mergeLinks(item.children || []);
    const url = safeUrl(item.url);
    if (!title || !url) continue;
    const previous = result.find(link => link.url === url && link.title === title);
    if (previous) previous.children = mergeLinks([...previous.children, ...children]);
    else result.push({ title, url, image: /^https?:\/\/|^\/\//.test(item.image || '') ? safeUrl(item.image) : '', children });
  }
  return result;
}

export function flattenNavigation(links) {
  return (links || []).flatMap(link => [link, ...flattenNavigation(link.children)]);
}

export function buildNavigation(input) {
  const links = mergeLinks(input);
  const identify = (items, prefix = 'link') => items.forEach((item, index) => {
    item.id = `${prefix}-${index}`;
    identify(item.children, item.id);
  });
  identify(links);
  const find = name => links.find(link => handle(link) === name);
  const bestseller = find('bestseller-1');
  const sale = find('sale');
  const newIn = find('new-in');
  const own = find('bekleidung');
  const brands = find('top-brands');
  const ownBrands = ownHandles.flatMap(name => brands?.children.filter(link => handle(link) === name) || []);
  const ownAll = brands?.children.find(link => handle(link) === 'eigenmarken');
  const otherBrands = brands?.children.filter(link => !ownHandles.includes(handle(link)) && link.url !== brands.url && link !== ownAll) || [];
  const featured = [bestseller, sale, newIn, own];
  const rank = link => categoryOrder.includes(handle(link)) ? categoryOrder.indexOf(handle(link)) : categoryOrder.length;
  const categories = links.filter(link => !featured.includes(link)).sort((a, b) => rank(a) - rank(b));
  return { links, priority: [bestseller, sale].filter(Boolean), bestseller, sale, newIn, own, brands, ownAll, ownBrands, otherBrands, categories };
}

export function searchNavigation(links, query, labels = {}) {
  const text = normalize(query);
  if (!text) return [];
  const seen = new Set();
  return flattenNavigation(links).filter(link => {
    const key = `${link.title}\n${link.url}`;
    const labelKey = { 'new-in': 'new_in', 'bestseller-1': 'bestsellers', bekleidung: 'own_labels', bundles: 'looks_sets', sale: 'sale' }[handle(link)];
    if (!normalize(`${link.title} ${labels[labelKey] || ''}`).includes(text) || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

if (typeof window !== 'undefined' && !customElements.get('eisdieler-navigation')) {
  customElements.define('eisdieler-navigation', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      try {
        const data = JSON.parse(this.querySelector('[data-navigation-source]').textContent);
        this.model = buildNavigation(data.links);
        if (!this.model.links.length) return;
        this.copy = data.labels;
        this.contact = safeUrl(data.contact);
        this.drawer = this.closest('.drawer');
        if (!this.drawer) return;
        this.scrollBody = this.closest('.drawer__body') || this.drawer.querySelector('.drawer__body');
        this.nodes = new Map(flattenNavigation(this.model.links).map(link => [link.id, link]));
        this.querySelectorAll(':scope > .ed-nav__view').forEach(view => view.remove());
        this.view = document.createElement('div');
        this.view.className = 'ed-nav__view';
        this.uid = `${this.drawer.id}-navigation`;
        this.stack = [];
        this.current = null;
        this.append(this.view);
        this.render();
        this.controller = new AbortController();
        const signal = this.controller.signal;
        this.addEventListener('click', event => this.onClick(event), { signal });
        this.addEventListener('input', event => this.onInput(event), { signal });
        this.addEventListener('error', event => {
          if (event.target.matches('.ed-nav__teaser img')) {
            event.target.closest('.ed-nav__teaser').classList.add('ed-nav__teaser--text');
            event.target.remove();
          }
        }, { capture: true, signal });
        this.wasOpen = this.drawer.classList.contains('is-active');
        this.observer = new MutationObserver(() => {
          const open = this.drawer.classList.contains('is-active');
          if (this.wasOpen && !open) { this.stack = []; this.current = null; this.render(); }
          this.wasOpen = open;
        });
        this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
        this.hidden = false;
        this.drawer.classList.add('ed-nav-ready');
      } catch (error) {
        this.view?.remove();
        this.controller?.abort();
        this.controller = null;
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.drawer?.classList.remove('ed-nav-ready', 'ed-nav-submenu');
      this.controller = null;
    }

    text(key, values = {}) {
      let result = this.copy[key] || '';
      for (const [name, value] of Object.entries(values)) result = result.replaceAll(`__${name}__`, String(value));
      return result;
    }

    arrow(kind = 'right') {
      return `<span class="ed-nav__arrow ed-nav__arrow--${kind}" aria-hidden="true">${kind === 'left' ? '←' : kind === 'chevron' ? '›' : '→'}</span>`;
    }

    title(link) {
      if (link === this.model.own) return this.text('own_labels');
      if (link === this.model.newIn) return this.text('new_in');
      if (link === this.model.sale) return this.text('sale');
      if (handle(link) === 'bundles') return this.text('looks_sets');
      return link.title;
    }

    anchor(link, className = 'ed-nav__link', label = this.title(link), content) {
      const own = link === this.model.own && this.model.ownBrands.length;
      const group = own ? 'own-labels' : link.children.length ? link.id : '';
      return `<a class="${className}" href="${escapeHtml(link.url)}"${group ? ` data-nav-group="${group}" aria-controls="${escapeHtml(this.uid)}-panel" aria-expanded="false"` : ''}>${content ?? `<span>${escapeHtml(label)}</span>${this.arrow(group ? 'chevron' : 'right')}`}</a>`;
    }

    direct(link, className, label) {
      return `<a class="${className}" href="${escapeHtml(link.url)}">${escapeHtml(label)}${this.arrow()}</a>`;
    }

    searchField(brands = false) {
      const label = this.text(brands ? 'search_brand' : 'search_menu');
      return `<label class="ed-nav__search"><span class="ed-nav__search-icon" aria-hidden="true"></span><input type="search" data-nav-search="${brands ? 'brands' : 'menu'}" aria-label="${escapeHtml(label)}" placeholder="${escapeHtml(label)}" autocomplete="off"></label>`;
    }

    main() {
      const priorities = this.model.priority.map(link => this.anchor(link, `ed-nav__feature${link === this.model.sale ? ' ed-nav__feature--sale' : ''}`, this.title(link), `<strong>${escapeHtml(this.title(link))}</strong><small>${escapeHtml(this.text(link === this.model.sale ? 'reduced' : 'bestsellers'))}${this.arrow()}</small>`)).join('');
      const entries = [this.model.newIn, this.model.own].filter(Boolean).map(link => this.anchor(link, 'ed-nav__discovery-link', this.title(link), `<strong>${escapeHtml(this.title(link))}</strong><small>${escapeHtml(link === this.model.newIn ? this.text('fresh') : this.model.ownBrands.length ? this.text('labels_count', { count: this.model.ownBrands.length }) : this.text('our_labels'))}${this.arrow(link === this.model.own && this.model.ownBrands.length ? 'chevron' : 'right')}</small>`)).join('');
      return `${this.searchField()}<div data-nav-main>${priorities ? `<nav class="ed-nav__priority" aria-label="${escapeHtml(this.text('priority_label'))}">${priorities}</nav>` : ''}${entries ? `<nav class="ed-nav__discovery" aria-label="${escapeHtml(this.text('discovery_label'))}">${entries}</nav>` : ''}<nav class="ed-nav__categories" aria-label="${escapeHtml(this.text('categories'))}">${this.model.categories.map(link => this.anchor(link)).join('')}</nav>${this.contact ? `<div class="ed-nav__footer"><a href="${escapeHtml(this.contact)}">${escapeHtml(this.text('contact'))}</a></div>` : ''}</div><section class="ed-nav__results" data-nav-results aria-live="polite" hidden></section>`;
    }

    submenuTitle() {
      return this.current === 'own-labels' ? this.text('own_labels') : this.title(this.nodes.get(this.current));
    }

    subhead() {
      const previous = this.stack.at(-1);
      const title = previous ? previous === 'own-labels' ? this.text('own_labels') : this.title(this.nodes.get(previous)) : this.text('menu');
      return `<div class="ed-nav__subhead"><button type="button" class="ed-nav__back" data-nav-back aria-label="${escapeHtml(this.text('back_to', { title }))}">${this.arrow('left')}</button><h2 id="${escapeHtml(this.uid)}-title">${escapeHtml(this.submenuTitle())}</h2></div>`;
    }

    brands(onlyOwn = false) {
      return `${this.subhead()}${onlyOwn ? '' : this.searchField(true)}<p class="ed-nav__status" data-nav-status aria-live="polite" hidden></p><section class="ed-nav__own" data-brand-section>${onlyOwn ? '' : `<h3>${escapeHtml(this.text('our_labels'))}</h3>`}<nav class="ed-nav__brand-grid" aria-label="${escapeHtml(this.text('our_labels'))}">${this.model.ownBrands.map(link => `<div data-brand-name="${escapeHtml(link.title)}">${this.anchor(link, 'ed-nav__brand-tile')}</div>`).join('')}</nav>${onlyOwn ? this.direct(this.model.own, 'ed-nav__all', this.text('all_collection', { title: this.text('own_labels') })) : this.model.ownAll ? this.direct(this.model.ownAll, 'ed-nav__all', this.text('all_own')) : ''}</section>${onlyOwn ? '' : `<section data-brand-section><h3>${escapeHtml(this.text('other_brands'))}</h3><nav class="ed-nav__other-brands" aria-label="${escapeHtml(this.text('other_brands'))}">${this.model.otherBrands.map(link => `<div data-brand-name="${escapeHtml(link.title)}">${this.anchor(link, 'ed-nav__brand-link')}</div>`).join('')}</nav></section>${this.direct(this.model.brands, 'ed-nav__all', this.text('all_brands'))}`}`;
    }

    group() {
      if (this.current === 'own-labels') return this.brands(true);
      const node = this.nodes.get(this.current);
      if (node === this.model.brands) return this.brands();
      const children = node.children.filter(link => link.url !== node.url);
      if (handle(node) === 'alle-bekleidung') children.sort((a, b) => Number(handle(b) === 'hosen') - Number(handle(a) === 'hosen'));
      const allLabel = this.text('all_collection', { title: this.title(node) });
      return `${this.subhead()}<a class="ed-nav__teaser${node.image ? '' : ' ed-nav__teaser--text'}" href="${escapeHtml(node.url)}">${node.image ? `<img src="${escapeHtml(node.image)}" alt="" width="76" height="96" loading="lazy">` : ''}<span>${escapeHtml(allLabel)}<small>${escapeHtml(this.text('collection'))}</small></span>${this.arrow()}</a><nav class="ed-nav__subgrid" aria-label="${escapeHtml(this.title(node))}">${children.map(link => this.anchor(link, 'ed-nav__category-link')).join('')}</nav>`;
    }

    render() {
      this.view.innerHTML = `<div id="${escapeHtml(this.uid)}-panel"${this.current ? ` aria-labelledby="${escapeHtml(this.uid)}-title"` : ''}>${this.current ? this.group() : this.main()}</div>`;
      this.drawer.classList.toggle('ed-nav-submenu', Boolean(this.current));
    }

    onClick(event) {
      const trigger = event.target.closest('[data-nav-group], [data-nav-back]');
      if (!trigger || !this.contains(trigger) || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      const former = this.current;
      if (trigger.hasAttribute('data-nav-back')) this.current = this.stack.pop() || null;
      else { this.stack.push(this.current); this.current = trigger.dataset.navGroup; }
      this.render();
      if (this.scrollBody) this.scrollBody.scrollTop = 0;
      const utils = window.themeCore?.utils;
      utils?.removeTrapFocus?.(this.drawer);
      utils?.trapFocus?.(this.drawer);
      const target = this.current ? this.querySelector('[data-nav-back]') : [...this.querySelectorAll('[data-nav-group]')].find(link => link.dataset.navGroup === former);
      target?.focus({ preventScroll: true });
    }

    onInput(event) {
      const input = event.target.closest('[data-nav-search]');
      if (!input) return;
      const query = normalize(input.value);
      if (input.dataset.navSearch === 'brands') {
        let count = 0;
        this.querySelectorAll('[data-brand-name]').forEach(item => { item.hidden = !normalize(item.dataset.brandName).includes(query); if (!item.hidden) count++; });
        this.querySelectorAll('[data-brand-section]').forEach(section => { section.hidden = ![...section.querySelectorAll('[data-brand-name]')].some(item => !item.hidden); });
        const status = this.querySelector('[data-nav-status]');
        status.hidden = !query;
        status.textContent = count ? this.text(count === 1 ? 'brand_result_one' : 'brand_results', { count }) : this.text('empty');
      } else {
        this.querySelector('[data-nav-main]').hidden = Boolean(query);
        const result = this.querySelector('[data-nav-results]');
        result.hidden = !query;
        if (!query) return;
        const matches = searchNavigation(this.model.links, query, this.copy);
        result.innerHTML = `<h3>${escapeHtml(this.text('results', { count: matches.length }))}</h3><nav class="ed-nav__categories">${matches.map(link => this.anchor(link, 'ed-nav__category-link')).join('')}</nav>${matches.length ? '' : `<p class="ed-nav__status">${escapeHtml(this.text('empty'))}</p>`}`;
      }
    }
  });
}
